import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { SectionHeader } from '@/components/ui/section-header';
import { copyToClipboard } from '@/lib/utils';
import { defaults as widgetDefaults, normaliseFontStack } from '@nearst/search-widget-shared';

// The retailer-facing URL for the bundled widget. In production this is
// served by `channels/services/search-widget` behind its `embed.near.st`
// API Gateway custom domain; local dev (`yarn staging` + `serverless offline`
// on that service) points at the offline URL via VITE_SEARCH_WIDGET_URL.
const WIDGET_SRC = import.meta.env.VITE_SEARCH_WIDGET_URL || 'https://embed.near.st/widget.js';

// Imported from the widget itself, never redeclared here. `buildSnippet`
// omits any attribute still set to its default, so a local copy that drifts
// produces a snippet whose omissions mean something different on the
// retailer's site than they did in the preview above it.
const DEFAULT_PRIMARY_COLOUR = widgetDefaults.primaryColour;
const DEFAULT_RADIUS = widgetDefaults.radius;
const DEFAULT_FONT_FAMILY = widgetDefaults.fontFamily;
const DEFAULT_PLACEHOLDER = widgetDefaults.placeholder;

// `font-family` only NAMES a font — it never loads one. The widget renders in
// a Shadow DOM on the retailer's own site, and `@font-face` is document-scoped
// and inherits into shadow trees, so naming a font their site already loads
// works and naming anything else silently falls back. That rules out a list of
// "available fonts": we'd be offering fonts we can't deliver, which would look
// right in the preview (this dashboard loads them) and wrong on their site.
// So the presets are limited to values that need no loading at all, with a
// free-text escape hatch for "my site already loads X".
const FONT_PRESETS = [
	{ label: 'Match my website (recommended)', value: DEFAULT_FONT_FAMILY },
	{ label: 'System default', value: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
	{ label: 'Classic sans', value: 'Helvetica, Arial, sans-serif' },
	{ label: 'Classic serif', value: 'Georgia, "Times New Roman", serif' }
];
const CUSTOM_FONT = '__custom__';

let widgetLoader = null;

// Idempotent script injector — one load per page, resolves once the custom
// element is registered. Any failure rejects (surfaced in the preview as a
// friendly fallback), later successful loads reset the cached rejection.
function loadWidgetScript() {
	if (widgetLoader) return widgetLoader;
	widgetLoader = new Promise((resolve, reject) => {
		if (typeof window === 'undefined') {
			reject(new Error('window-unavailable'));
			return;
		}
		if (window.customElements?.get('nearst-search')) {
			resolve();
			return;
		}
		const script = document.createElement('script');
		script.src = WIDGET_SRC;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () => {
			widgetLoader = null;
			reject(new Error('script-load-failed'));
		};
		document.head.appendChild(script);
	});
	return widgetLoader;
}

// Escape a value for interpolation into an HTML attribute inside the copyable
// snippet. `fontFamily` in particular is a free-text CSS stack — a natural
// value like `"Helvetica Neue", Arial` needs its inner quotes encoded or the
// retailer's paste ships malformed HTML. Also matters for `placeholder`
// values containing `&`/`<`/`>`.
const HTML_ATTR_ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
function escapeAttr(value) {
	return String(value ?? '').replace(/[&<>"]/g, (c) => HTML_ATTR_ESC[c]);
}

function buildSnippet({ shopId, shopSlug, primaryColour, radius, fontFamily, placeholder }) {
	const attrs = [`shop-id="${escapeAttr(shopId)}"`, `shop-slug="${escapeAttr(shopSlug)}"`];
	if (primaryColour && primaryColour !== DEFAULT_PRIMARY_COLOUR) attrs.push(`primary-colour="${escapeAttr(primaryColour)}"`);
	if (radius && radius !== DEFAULT_RADIUS) attrs.push(`radius="${escapeAttr(radius)}"`);
	if (fontFamily && fontFamily !== DEFAULT_FONT_FAMILY) attrs.push(`font-family="${escapeAttr(fontFamily)}"`);
	if (placeholder && placeholder !== DEFAULT_PLACEHOLDER) attrs.push(`placeholder="${escapeAttr(placeholder)}"`);
	return `<script src="${WIDGET_SRC}" async></script>\n<nearst-search ${attrs.join(' ')}></nearst-search>`;
}

export function SearchWidgetInstall({ shopId, shopSlug, helpUrl }) {
	const [primaryColour, setPrimaryColour] = useState(DEFAULT_PRIMARY_COLOUR);
	const [radius, setRadius] = useState(DEFAULT_RADIUS);
	const [fontFamily, setFontFamily] = useState(DEFAULT_FONT_FAMILY);
	const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER);
	const [fontIsCustom, setFontIsCustom] = useState(false);
	const [copied, setCopied] = useState(false);
	const [widgetState, setWidgetState] = useState('loading');
	const previewRef = useRef(null);
	const copiedTimerRef = useRef(null);

	// What the widget and the snippet actually get. Presets already carry a
	// fallback and `inherit` is passed through untouched, so this only bites on
	// a hand-typed family.
	const appliedFontFamily = normaliseFontStack(fontFamily);

	useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

	useEffect(() => {
		let cancelled = false;
		loadWidgetScript()
			.then(() => {
				if (!cancelled) setWidgetState('ready');
			})
			.catch(() => {
				if (!cancelled) setWidgetState('failed');
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Attributes reflect straight into the mounted custom element — the widget
	// re-renders its shadow DOM on attribute change (see `observedAttributes`
	// in nearst-search.js), so no remount needed.
	useEffect(() => {
		const el = previewRef.current;
		if (!el || widgetState !== 'ready') return;
		el.setAttribute('shop-id', shopId ?? '');
		el.setAttribute('shop-slug', shopSlug ?? '');
		el.setAttribute('primary-colour', primaryColour);
		el.setAttribute('radius', radius);
		el.setAttribute('font-family', appliedFontFamily);
		el.setAttribute('placeholder', placeholder);
	}, [widgetState, shopId, shopSlug, primaryColour, radius, appliedFontFamily, placeholder]);

	const snippet = useMemo(
		() => buildSnippet({ shopId, shopSlug, primaryColour, radius, fontFamily: appliedFontFamily, placeholder }),
		[shopId, shopSlug, primaryColour, radius, appliedFontFamily, placeholder]
	);

	// A value that doesn't match a preset (restored state, or typed by hand)
	// has to open the custom field on its own, or it would be unreachable.
	const showCustomFont = fontIsCustom || !FONT_PRESETS.some((preset) => preset.value === fontFamily);

	const handleFontSelect = (value) => {
		if (value === CUSTOM_FONT) {
			// Keep the current family so the text field starts from it.
			setFontIsCustom(true);
			return;
		}
		setFontIsCustom(false);
		setFontFamily(value);
	};

	const handleCopy = async () => {
		if (await copyToClipboard(snippet)) {
			setCopied(true);
			clearTimeout(copiedTimerRef.current);
			copiedTimerRef.current = setTimeout(() => setCopied(false), 1800);
		}
	};

	return (
		<section className="space-y-6">
			<SectionHeader
				eyebrow="Install"
				title="Add the search widget to your website"
				sub="Paste this snippet anywhere on your site — the widget searches your live in-store inventory, and clicks open the product on your Local Storefront."
			/>

			<SoftCard className="space-y-4 !p-5 md:!p-6">
				<div>
					<h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Preview</h4>
					<p className="mt-1 text-xs text-muted-foreground">Live results from your shop's Local Storefront.</p>
				</div>
				<div className="rounded-xl border border-border bg-card p-6">
					{widgetState === 'failed' ? (
						<p className="text-sm text-muted-foreground">
							Preview unavailable in this environment. The snippet below will still work on your own site.
						</p>
					) : (
						<nearst-search ref={previewRef} />
					)}
				</div>
			</SoftCard>

			<SoftCard className="space-y-5 !p-5 md:!p-6">
				<div>
					<h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Customise</h4>
					<p className="mt-1 text-xs text-muted-foreground">Changes update the preview and snippet instantly.</p>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<label className="block space-y-1.5">
						<span className="text-xs font-semibold text-foreground">Primary colour</span>
						<div className="flex items-center gap-3">
							<input
								type="color"
								value={primaryColour}
								onChange={(event) => setPrimaryColour(event.target.value)}
								className="h-9 w-14 cursor-pointer rounded-md border border-border bg-card p-1"
							/>
							<input
								type="text"
								value={primaryColour}
								onChange={(event) => setPrimaryColour(event.target.value)}
								className="min-w-0 flex-1 rounded-md border border-border bg-card px-3 py-2 font-mono text-xs text-foreground"
							/>
						</div>
					</label>

					<label className="block space-y-1.5">
						<span className="text-xs font-semibold text-foreground">Corner radius ({radius}px)</span>
						<input
							type="range"
							min="0"
							max="24"
							value={radius}
							onChange={(event) => setRadius(event.target.value)}
							className="w-full"
						/>
					</label>

					<div className="block space-y-1.5">
						<label htmlFor="widget-font" className="text-xs font-semibold text-foreground">
							Font
						</label>
						<select
							id="widget-font"
							value={showCustomFont ? CUSTOM_FONT : fontFamily}
							onChange={(event) => handleFontSelect(event.target.value)}
							className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
						>
							{FONT_PRESETS.map((preset) => (
								<option key={preset.value} value={preset.value}>
									{preset.label}
								</option>
							))}
							<option value={CUSTOM_FONT}>Something else…</option>
						</select>
						{showCustomFont ? (
							<input
								type="text"
								value={fontFamily}
								onChange={(event) => setFontFamily(event.target.value)}
								placeholder="e.g. Poppins, sans-serif"
								aria-label="Custom font family"
								className="w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-xs text-foreground"
							/>
						) : null}
						<p className="text-[11px] leading-snug text-muted-foreground">
							{showCustomFont ? (
								<>
									Your website must already load this font — otherwise the widget falls back to{' '}
									<span className="font-mono">sans-serif</span>. Used as:{' '}
									<span className="font-mono break-all">{appliedFontFamily}</span>
								</>
							) : fontFamily === DEFAULT_FONT_FAMILY ? (
								'The widget takes the font from the page it sits on, so it matches the rest of your website.'
							) : (
								'A built-in font that works on any website — nothing for your site to load.'
							)}
						</p>
					</div>

					<label className="block space-y-1.5">
						<span className="text-xs font-semibold text-foreground">Placeholder text</span>
						<input
							type="text"
							value={placeholder}
							onChange={(event) => setPlaceholder(event.target.value)}
							className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
						/>
					</label>
				</div>
			</SoftCard>

			<SoftCard className="space-y-3 !p-5 md:!p-6">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div>
						<h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Embed snippet</h4>
						<p className="mt-1 text-xs text-muted-foreground">
							Paste anywhere in your site's HTML — usually just above the closing body tag.
						</p>
					</div>
					{helpUrl ? (
						<a
							href={helpUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 text-xs font-semibold text-action hover:underline"
						>
							Install guide <ExternalLink className="h-3 w-3" />
						</a>
					) : null}
				</div>
				<pre className="overflow-x-auto rounded-md border border-border bg-muted px-3 py-3 font-mono text-xs leading-relaxed text-foreground">
					{snippet}
				</pre>
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
				>
					{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
					{copied ? 'Snippet copied' : 'Copy snippet'}
				</button>
			</SoftCard>
		</section>
	);
}
